import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { DxButtonModule, DxDataGridModule, DxLoadIndicatorModule, DxRadioGroupModule } from 'devextreme-angular';
import { alert } from 'devextreme/ui/dialog';
import { BlockingPromotion, PlanningMethod, PlanningMethodConflictData } from '../../models/catalogue-planner.model';
import { CatalogueMethodSettingsService } from '../../services/catalogue-method-settings.service';
import { ConfirmationService } from '../../services/confirmation.service';
import { CanComponentDeactivate } from '../../services/can-deactivate.model';

interface MethodOption {
  value: PlanningMethod;
  text: string;
  description: string;
}

@Component({
  selector: 'app-planning-method',
  standalone: true,
  imports: [CommonModule, DxRadioGroupModule, DxButtonModule, DxDataGridModule, DxLoadIndicatorModule],
  templateUrl: './planning-method.component.html',
  styleUrl: './planning-method.component.css'
})
export class PlanningMethodComponent implements OnInit, CanComponentDeactivate {
  readonly options: MethodOption[] = [
    { value: 'slotting', text: 'Slotting board', description: 'Keep the existing slotting planning workflow.' },
    { value: 'catalogue', text: 'Catalogue pages', description: 'Plan authorised promotion products on named category pages.' }
  ];

  method: PlanningMethod = 'slotting';
  /** The saved method; null when the saved value is invalid, so either choice can be saved. */
  original: PlanningMethod | null = 'slotting';
  canManage = false;
  loading = true;
  saving = false;
  saved = false;
  error = '';
  saveError = '';
  /** Set when a switch is refused because open promotions hold the other method's lines (409). */
  blockingPromotions: BlockingPromotion[] = [];
  blockingCount = 0;

  constructor(
    private readonly settings: CatalogueMethodSettingsService,
    private readonly confirmation: ConfirmationService
  ) {}

  get catalogueEnabled(): boolean { return this.method === 'catalogue'; }
  get dirty(): boolean { return this.method !== this.original; }

  ngOnInit(): void { void this.load(); }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const settings = await this.settings.getSettings();
      this.method = settings.method;
      this.original = settings.method;
      this.canManage = settings.canManage;
    } catch (error: any) {
      if (error?.error?.data?.canManage === true) {
        // Invalid saved method: let the administrator pick and save a valid one.
        this.original = null;
        this.canManage = true;
        this.saveError = this.serverMessage(error);
      } else {
        this.error = 'Could not load tenant planning settings. Please retry.';
      }
    }
    finally { this.loading = false; }
  }

  selectMethod(value: PlanningMethod): void {
    if (!this.canManage || this.saving || value === this.method) return;
    this.method = value;
    this.saved = false;
    this.saveError = '';
    this.clearBlocking();
  }

  async save(): Promise<void> {
    if (!this.canManage || this.saving || !this.dirty) return;
    this.saving = true;
    this.saved = false;
    this.saveError = '';
    this.clearBlocking();
    try {
      await this.settings.save(this.catalogueEnabled);
      this.original = this.method;
      this.saved = true;
    } catch (error: any) {
      const conflict = this.conflictData(error);
      if (conflict) {
        // The switch was refused: stay on the method the tenant actually uses.
        this.method = this.original ?? this.method;
        this.blockingPromotions = conflict.blockingPromotions;
        this.blockingCount = Math.max(conflict.blockingCount ?? 0, conflict.blockingPromotions.length);
        this.saveError = this.serverMessage(error)
          || 'Close or remove the open promotions that block this switch before changing the planning method.';
      } else {
        this.saveError = this.serverMessage(error) || 'Settings were not saved. Check your tenant access and retry.';
      }
    } finally { this.saving = false; }
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (this.saving) {
      void alert('The planning method is still saving. Please wait.', 'Saving');
      return false;
    }
    return this.dirty ? this.confirmation.confirmUnsavedChangesNavigation() : true;
  }

  private clearBlocking(): void {
    this.blockingPromotions = [];
    this.blockingCount = 0;
  }

  private conflictData(error: any): PlanningMethodConflictData | null {
    const data = error?.error?.data;
    return error?.status === 409 && Array.isArray(data?.blockingPromotions) ? data : null;
  }

  private serverMessage(error: any): string {
    return typeof error?.error?.message === 'string' ? error.error.message : '';
  }
}
